import React, { useState } from 'react';
import { api } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';

// NAME_RE mirrors the backend rule (apps/client/handlers_projects.go
// validateProjectName, PIN-172): a Unicode letter/digit at each end, with
// letters, digits, spaces and . _ - allowed in the middle. Length (3–64 runes)
// is checked separately. Kept in sync so the user sees a localized message
// instead of the raw English API error.
const NAME_RE = /^[\p{L}\p{N}][\p{L}\p{N} _.-]*[\p{L}\p{N}]$/u;

function validateName(name, t) {
  const trimmed = name.trim();
  const len = [...trimmed].length; // codepoints, to match the backend's rune count
  if (len < 3 || len > 64) return t('projectModal.errors.length');
  if (!NAME_RE.test(trimmed)) return t('projectModal.errors.invalidChars');
  return '';
}

// localizeCreateError maps known backend error strings to localized messages so
// the RU UI never surfaces raw English; unknown errors fall back to a generic.
function localizeCreateError(d, t) {
  const e = (d && d.error) || '';
  if (e === 'email_not_verified') return t('verifyBanner.text');
  if (e === 'project_name_taken') return t('projectModal.errors.taken');
  if (e.includes('3-64')) return t('projectModal.errors.length');
  if (e.includes('invalid characters')) return t('projectModal.errors.invalidChars');
  if (e.includes('limit')) return t('projectModal.errors.limit');
  return t('projectModal.failedToCreate');
}

const CreateProjectModal = ({ onClose, onCreate }) => {
  const [name, setName]     = useState('');
  const [error, setError]   = useState('');
  const [saving, setSaving] = useState(false);
  const { t } = useI18n();

  const handleSubmit = async (e) => {
    e.preventDefault();
    const localErr = validateName(name, t);
    if (localErr) { setError(localErr); return; }
    setError('');
    setSaving(true);
    const res = await api.createProject(name.trim());
    if (res.ok) {
      const p = await res.json();
      onCreate(p);
      onClose();
    } else {
      const d = await res.json().catch(() => ({}));
      setError(localizeCreateError(d, t));
      setSaving(false);
    }
  };

  return (
    <div className="uh-modal-backdrop" onClick={onClose}>
      <div className="uh-modal" onClick={e => e.stopPropagation()}>
        <div className="uh-modal-title">{t('projectModal.title')}</div>
        <p className="uh-muted" style={{ marginTop: 0, fontSize: '0.9em' }}>{t('projectModal.hint')}</p>
        {error && <div className="uh-alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="uh-form-group">
            <label className="uh-label">{t('projectModal.projectName')}</label>
            <input className="uh-input" value={name} onChange={e => setName(e.target.value)} required autoFocus />
            <div className="uh-muted" style={{ fontSize: '0.8em', marginTop: 4 }}>{t('projectModal.nameHelp')}</div>
          </div>
          <div className="uh-modal-footer">
            <button type="button" className="uh-btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="submit" className="uh-btn uh-btn-primary" disabled={saving}>
              {saving ? t('common.creating') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProjectModal;
